import { registerQuery, registerMutation } from 'data-fetching-client';
import http from 'hub-http/clients/apiClient';
import { DropZoneNoBrowseFileAccess } from 'FileManagerLib/enums/FileAccess';
import { NOTE } from 'customer-data-objects/engagement/EngagementTypes';
import { fileSize } from 'FileManagerCore/utils/fileSize';
import { formatDate, getFormattedSource } from '../utils/attachmentsUtils';
import userInfo from 'hub-http/userInfo';
import Raven from 'raven-js';
import { SOURCE_RAW_VALUES } from '../constants/constants';
const FILEMANAGER_V2_URI = 'filemanager/api/v2/files';
const FILEMANAGER_V3_URI = 'filemanager/api/v3/files';
const CRM_RECORDS_FOLDER_PATH = 'record-attachments';
const transformSource = source => {
  if (source === SOURCE_RAW_VALUES.FORWARDED_EMAIL || source === SOURCE_RAW_VALUES.INCOMING_EMAIL) {
    return SOURCE_RAW_VALUES.EMAIL;
  }
  return source;
};
const convertAttachmentToAttachmentRow = attachment => {
  const idString = String(attachment.file.id);
  const source = transformSource(attachment.source);
  return Object.assign({}, attachment.file, {
    // From engagements backend
    source,
    engagementId: attachment.engagementId,
    // Convenience fields for display
    idString,
    sizeFormatted: fileSize(attachment.file.size),
    createdFormatted: formatDate(attachment.file.created),
    sourceFormatted: getFormattedSource(source)
  });
};
export const fetchAttachmentsQuery = registerQuery({
  fieldName: 'fetchAttachments',
  args: ['objectTypeId', 'objectId', 'count', 'offset', 'attachmentIds'],
  fetcher: async ({
    objectTypeId,
    objectId,
    count,
    offset = 0,
    attachmentIds = null,
    origin,
    shouldBustCache = false
  }) => {
    const attachmentsResponse = await http.post(`crm-record-attachments/v1/attachments/${objectTypeId}/${objectId}/batch`, {
      data: {
        count,
        offset,
        attachmentIds,
        regenerate: shouldBustCache
      },
      query: {
        origin
      }
    });
    const attachmentRows = attachmentsResponse.results.map(convertAttachmentToAttachmentRow);
    return {
      attachments: attachmentRows,
      total: attachmentsResponse.total,
      hasMore: attachmentsResponse.hasMore
    };
  }
});
export const createEngagementMutation = registerMutation({
  fieldName: 'createEngagement',
  args: ['fmFileId', 'objectId', 'fetchOwnerId', 'fetchNoteAssDef'],
  fetcher: async ({
    fmFileId,
    objectId,
    fetchOwnerId,
    fetchNoteAssDef
  }) => {
    const [ownerId, noteAssDef] = await Promise.all([fetchOwnerId, fetchNoteAssDef]);
    const newEngagement = {
      metadata: {
        body: ''
      },
      engagement: {
        type: NOTE,
        ownerId
      }
    };
    const response = await http.post('engagements/v2/engagements', {
      data: Object.assign({}, newEngagement, {
        object: {
          properties: [{
            name: 'hs_engagement_type',
            value: NOTE
          }, {
            name: 'hs_timestamp'
          }, {
            name: 'hubspot_owner_id',
            value: ownerId
          }, {
            name: 'hs_at_mentioned_owner_ids',
            value: ''
          }, {
            name: 'hs_engagement_source',
            value: 'CRM_UI'
          }, {
            name: 'hs_attachment_ids',
            value: fmFileId
          }]
        },
        objectsToAssociate: [{
          associationSpec: {
            associationCategory: noteAssDef.category,
            associationTypeId: noteAssDef.inverseId
          },
          objectIds: [objectId]
        }]
      })
    });
    return {
      engagementId: response.objectId
    };
  }
});
export const fetchCurrentOwnerQuery = registerQuery({
  fieldName: 'fetchCurrentOwner',
  args: [],
  fetcher: async () => {
    return await http.get(`owners/v2/owners/current`);
  }
});
export const fetchNoteAssociationDefinitionQuery = registerQuery({
  fieldName: 'fetchNoteAssociationDefinition',
  args: ['objectTypeId'],
  fetcher: async ({
    objectTypeId
  }) => {
    return await http.get(`associations/v1/definitions/${objectTypeId}/${NOTE}`);
  }
});
export const uploadFileMutation = registerMutation({
  fieldName: 'uploadFile',
  args: ['fileFromDisk', 'progressCallback'],
  fetcher: async ({
    fileFromDisk,
    progressCallback
  }) => {
    const formData = new FormData();
    formData.append('file', fileFromDisk);
    formData.append('folderPath', CRM_RECORDS_FOLDER_PATH);
    formData.append('options', JSON.stringify({
      access: DropZoneNoBrowseFileAccess.HIDDEN_IN_APP_SENSITIVE
    }));
    const uploadedFmFile = await http.post(`${FILEMANAGER_V3_URI}/upload`, {
      data: formData,
      headers: {
        'content-type': false
      },
      timeout: 0,
      withXhr: xhr => {
        xhr.upload.addEventListener('progress', event => progressCallback(event, fileFromDisk), false);
      }
    });
    // Backend may return a different filename than what was uploaded if there is a name conflict (e.g. file was already uploaded to hubspot previously)
    // This could be an appended "-1" or "-2" or "-<upload date>", based on how many times the same name has been uploaded
    return {
      uploadedFmFile: uploadedFmFile.objects[0]
    };
  }
});
export const renameAttachmentMutation = registerMutation({
  fieldName: 'renameAttachment',
  args: ['idString', 'newAttachmentName'],
  fetcher: ({
    idString,
    newAttachmentName
  }) => {
    return http.post(`${FILEMANAGER_V2_URI}/${idString}/move-file`, {
      data: {
        name: newAttachmentName
      }
    });
  }
});
export const userInfoQuery = registerQuery({
  fieldName: 'userInfo',
  fetcher: userInfo
});
export const downloadAttachmentsMutation = registerMutation({
  fieldName: 'downloadAttachments',
  args: ['email', 'selectedFiles'],
  fetcher: ({
    email,
    selectedFiles
  }) => {
    return http.post(`${FILEMANAGER_V2_URI}/export-all-files`, {
      data: {
        email,
        folder_paths: [],
        file_ids: Array.from(selectedFiles),
        include_hidden: true
      }
    }).catch(error => {
      Raven.captureException(error);
      throw error;
    });
  }
});